import React, { useRef, useState, useEffect, useLayoutEffect, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { Dropdown } from "react-bootstrap";
import ToastMessage from "../../ToastMessage";
import Can from "../../../permissions/Can";
import { PERMISSIONS } from "../../../permissions/permissions";
import { IMAGE_CATEGORIES, CATEGORY_NORMALIZATION, normalizeCategory } from "../../utils/imageCategories";
import { ReportTemplateContext } from "../ReportTemplate/ReportTemplateContext.js";
import { useImageHandler, invalidateMetadataCache } from "../../utils/ImageHandler";
import {
  uploadKiinteistoImage,
  deleteKiinteistoImage,
  updateKiinteistoImage,
  reorderKiinteistoImages,
  getImageSubcategories,
} from "../../../api/api";
import ImageModal from "../../utils/ImageModal";
import { useFailedImages } from "../../../context/ImageQueueContext";
import AnnotationModal from "./AnnotationModal";
import ConfirmModal from "../../ConfirmModal";


const DYNAMIC_IMAGE_CATEGORIES = new Set([
  "Ikkunat kuntoarvio",
  "Ovet kuntoarvio",
  "Julkisivut kuntoarvio",
  "Parvekkeet kuntoarvio",
  "Vesikatot kuntoarvio",
]);

const IKKUNA_SUBCATEGORY_TYPES = {
  "Asuntojen ikkunat":        "asuntojen_ikkunat",
  "Yleisten tilojen ikkunat": "yleisten_tilojen_ikkunat",
  "Asuntojen parvekeovet":    "asuntojen_parvekeovet",
  "Yleisten tilojen ulko-ovet": "yleisten_tilojen_ulko-ovet",
};

function findHiddenSubcategories(sections) {
  if (!sections?.length) return new Set();
  let hiddenIds = new Set();
  for (const section of sections) {
    try {
      const content = typeof section.content === "string"
        ? JSON.parse(section.content)
        : section.content || { blocks: [] };
      for (const block of (content.blocks || [])) {
        if (block.type === "SectionSelectionBlock" && block.data?.hiddenSectionIds?.length) {
          hiddenIds = new Set(block.data.hiddenSectionIds.map(Number));
          break;
        }
      }
    } catch {}
  }
  if (!hiddenIds.size) return new Set();
  const hidden = new Set();
  for (const [subName, sectionType] of Object.entries(IKKUNA_SUBCATEGORY_TYPES)) {
    const match = sections.find(s => s.type === sectionType);
    if (match && hiddenIds.has(Number(match.id))) hidden.add(subName);
  }
  return hidden;
}

export default function ImageBlock({ data, onChange, uiLimit = 8, propertyId, maxSelectable = null, mode="report", blockPath}) {
  const gridRef = useRef();
  const SYSTEM_USER_ID = 15;
  const textareaRefs = useRef({});
  // Guards the self-healing retry below against looping forever if a
  // re-fetch also fails — one retry per image id is enough to recover from
  // the common case (its blob: URL got evicted from the shared in-memory
  // LRU cache, which is capped well below large subcategories' image
  // counts, while imageUrls state still points at the now-revoked URL).
  const imgRetriedRef = useRef(new Set());

  const [thermalAnalysis, setThermalAnalysis] = useState(null);
  const [excludedImages, setExcludedImages] = useState(data?.excludedImages || []);
  const localExcludedChangeRef = useRef(false);
  const [dragSrcIndex, setDragSrcIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [portraitImages, setPortraitImages] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', bg: 'success' });
  const [loadingImages, setLoadingImages] = useState({});
  const subCategoryIsLocked = data?.subCategoryIsLocked || false;
  const [toggleAllConfirm, setToggleAllConfirm] = useState(null); // "hide" | "show" | null
  const [isPaginated, setIsPaginated] = useState(false);
  const [flashUrls, setFlashUrls] = useState([]);
  const [newImageIds, setNewImageIds] = useState(new Set());

  const mainCategories = IMAGE_CATEGORIES ? Object.keys(IMAGE_CATEGORIES) : [];
  const [category, setCategory] = useState(data?.category || "");
  const [subCategory, setSubCategory] = useState(data?.subCategory ?? null);
  // Keyed by category name, not just "whichever is the current filter" —
  // the grid can show images from many dynamic categories at once (e.g.
  // the "Kaikki" view), so each image needs its OWN category's
  // subcategories available, not only the one currently selected above.
  const [dynamicSubCategoriesByCategory, setDynamicSubCategoriesByCategory] = useState({});
  const isDynamicCategory = DYNAMIC_IMAGE_CATEGORIES.has(category);
  const subCategories = isDynamicCategory
    ? (dynamicSubCategoriesByCategory[category] || [])
    : (category && IMAGE_CATEGORIES[category] ? IMAGE_CATEGORIES[category] : []);

  const reportCtx = useContext(ReportTemplateContext);
  const reportType = reportCtx?.report?.type;
  const showKuntoarvioCategories =
    reportType === 'Ikkunoiden_ja_Ulko-ovien_Kuntoarvio' ||
    reportType === 'julkisivujen_kuntotutkimus' ||
    reportType === 'julkisivujen_kuntotutkimus_v2' ||
    reportType === 'ikkuna_ovi_raportti' ||
    reportType === 'ikkunoiden_ja_ulko-ovien_kuntoarvio';

  const hiddenSubcategories = useMemo(
    () => findHiddenSubcategories(reportCtx?.sections),
    [reportCtx?.sections]
  );
  const effectiveSubCategories = useMemo(
    () => subCategories.filter(sub => !hiddenSubcategories.has(sub)),
    [subCategories, hiddenSubcategories]
  );

  const [allChecked, setAllChecked] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [drawingImage, setDrawingImage] = useState(null);



  const openModal = (img) => {
    setModalImage(img);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalImage(null);
  };

  const params = useParams();
  const urlKiinteistoId = propertyId || params.id;
  const azureBase = process.env.REACT_APP_AZURE_BLOB_BASE;


  //IMage handler call

  const { availableImages, imageUrls, isLoading, fetchSasUrlWithCache, setAvailableImages, loadImages } =
    useImageHandler(urlKiinteistoId, { category, subCategory });

  const {failedImages, addFailedImage} = useFailedImages()

  // ----------------------------------
    
  const resizeTextarea = id => {
    const textarea = textareaRefs.current[id];
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  };

  const isPortraitWithRotation = (w, h, r = 0) => {
    const rot = ((r % 360) + 360) % 360;
    if (rot === 90 || rot === 270) return w > h;
    return h > w;
  };

  useEffect(() => {
    loadImages(false); // use cache if available; server fetch only if not cached
    setPage(1);
  }, [category, subCategory]);

  // The offline image queue (ImageQueueContext) is a single global
  // singleton with no reference to this (or any) ImageBlock instance, so
  // a successful retry from its modal broadcasts this event instead
  // (same pattern api.js already uses for 'ktp:ratelimit') — refresh only
  // when it's for this property, so other open ImageBlocks don't refetch
  // needlessly.
  useEffect(() => {
    const handleImageUploaded = e => {
      if (e.detail?.propertyId === urlKiinteistoId) loadImages(true);
    };
    window.addEventListener("ktp:imageUploaded", handleImageUploaded);
    return () => window.removeEventListener("ktp:imageUploaded", handleImageUploaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKiinteistoId]);

  // NOT auto-enabled for large sets: pagination only renders the current
  // page's images, but drag-and-drop reordering (handleDragStart/Over/Drop,
  // moveImage) works by index into the full sortedImages list — a paginated
  // view makes it impossible to drag an image to/from a different page, so
  // pagination has to stay an explicit, manual opt-in (the toggle button
  // below), never a default, regardless of image count.

  // Resize every mounted textarea once when the image SET changes (fresh
  // load, upload, delete) — not on every keystroke. Editing a caption only
  // replaces one object inside availableImages (same length), so keying off
  // .length instead of the array itself avoids re-measuring up to 226
  // textareas (forced synchronous layout, read+write per textarea) on every
  // single character typed. The textarea actually being edited already gets
  // resized directly inside handleDescriptionChange below.
  useLayoutEffect(() => {
    Object.keys(textareaRefs.current).forEach(resizeTextarea);
  }, [availableImages.length]);

  useEffect(() => {
    if (data?.category !== undefined) setCategory(CATEGORY_NORMALIZATION[data.category] || data.category || "");
    setSubCategory(data?.subCategory ?? null);
  }, [data?.category, data?.subCategory]);

  useEffect(() => {
    if (!isDynamicCategory || dynamicSubCategoriesByCategory[category]) return;
    getImageSubcategories(category)
      .then(res => setDynamicSubCategoriesByCategory(prev => ({ ...prev, [category]: res.data.map(r => r.subcategory) })))
      .catch(() => setDynamicSubCategoriesByCategory(prev => ({ ...prev, [category]: [] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, isDynamicCategory]);

  // Same fetch, but for every dynamic category actually present among the
  // currently loaded images — fixes per-card subcategory dropdowns being
  // blank whenever an image's own category isn't the selected filter
  // (e.g. the "Kaikki" view, where the filter is empty for every image).
  useEffect(() => {
    const missing = [...new Set(
      availableImages
        .map(img => normalizeCategory(img.metadata?.category))
        .filter(cat => DYNAMIC_IMAGE_CATEGORIES.has(cat) && !dynamicSubCategoriesByCategory[cat])
    )];
    missing.forEach(cat => {
      getImageSubcategories(cat)
        .then(res => setDynamicSubCategoriesByCategory(prev => ({ ...prev, [cat]: res.data.map(r => r.subcategory) })))
        .catch(() => setDynamicSubCategoriesByCategory(prev => ({ ...prev, [cat]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableImages]);

  useEffect(() => {
    if (localExcludedChangeRef.current) { localExcludedChangeRef.current = false; return; }
    setExcludedImages(data?.excludedImages || []);
  }, [data?.excludedImages]);


  useEffect(() => {
    if (!newImageIds.size) return;
    const urls = [...newImageIds].map(id => imageUrls[id]).filter(Boolean);
    if (!urls.length) return;
    setFlashUrls(urls);
    const t = setTimeout(() => setFlashUrls([]), 2500);
    return () => clearTimeout(t);
  }, [newImageIds, imageUrls]);

  const showToast = (message, bg = 'success') => setToast({ show: true, message, bg });

  const handleRotateImage = async imgId => {
    const img = availableImages.find(i => i.id === imgId);
    if (!img) return;

    const currentRotation = img.metadata?.rotation || 0;
    const newRotation = (currentRotation + 90) % 360;

    try {
      // Merge all existing metadata and update rotation
      const updatedMetadata = { ...img.metadata, rotation: newRotation };

      await updateKiinteistoImage(imgId, { metadata: updatedMetadata, updated_by: SYSTEM_USER_ID });

      setAvailableImages(prev =>
        prev.map(i => i.id === imgId ? { ...i, metadata: updatedMetadata } : i)
      );

      // Update portrait flag based on rotation
      setPortraitImages(prev => ({
        ...prev,
        [imgId]: isPortraitWithRotation(img.metadata.width, img.metadata.height, newRotation)
      }));

      invalidateMetadataCache(urlKiinteistoId);
      showToast(`Kuva käännetty ${newRotation}°`, "success");
    } catch (err) {
      console.error(err);
      showToast("Kuvan kääntö epäonnistui", "danger");
    }
  };

  const isPortrait = (img, naturalWidth, naturalHeight) => {
    const w = naturalWidth || img.metadata?.width || 1;
    const h = naturalHeight || img.metadata?.height || 1;
    const r = img.metadata?.rotation || 0;

    const rot = ((r % 360) + 360) % 360;
    if (rot === 90 || rot === 270) return w > h;
    return h > w;
  };



  const moveImage = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sortedImages.length) return;
    if (excludedImages.includes(sortedImages[index].id) !== excludedImages.includes(sortedImages[newIndex].id)) return;

    const newImages = [...sortedImages];
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    setAvailableImages(newImages);

    try {
      await reorderKiinteistoImages({ id_kiinteisto: urlKiinteistoId, category, subCategory, orderedIds: newImages.map(img => img.id) });
      invalidateMetadataCache(urlKiinteistoId);
      showToast("Kuvien järjestys päivitetty", "success");
    } catch (err) {
      console.error("Failed to reorder images:", err);
      showToast("Kuvien järjestyksen päivitys epäonnistui", "danger");
    }
  };

  const handleDragStart = (e, index) => {
    setDragSrcIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragSrcIndex === null || dragSrcIndex === index) return;
    if (excludedImages.includes(sortedImages[dragSrcIndex]?.id) !== excludedImages.includes(sortedImages[index]?.id)) return;
    setDragOverIndex(index);
  };

  const handleDrop = async (e, toIndex) => {
    e.preventDefault();
    const from = dragSrcIndex;
    setDragSrcIndex(null);
    setDragOverIndex(null);
    if (from === null || from === toIndex) return;
    if (excludedImages.includes(sortedImages[from]?.id) !== excludedImages.includes(sortedImages[toIndex]?.id)) return;

    const newImages = [...sortedImages];
    const [moved] = newImages.splice(from, 1);
    newImages.splice(toIndex, 0, moved);
    setAvailableImages(newImages);

    try {
      await reorderKiinteistoImages({ id_kiinteisto: urlKiinteistoId, category, subCategory, orderedIds: newImages.map(img => img.id) });
      invalidateMetadataCache(urlKiinteistoId);
      showToast("Kuvien järjestys päivitetty", "success");
    } catch (err) {
      console.error("Failed to reorder images:", err);
      showToast("Kuvien järjestyksen päivitys epäonnistui", "danger");
    }
  };
const analyzeThermalImage = async (file) => {
  const formData = new FormData();

  formData.append("image", file);

  const API_URL =
    process.env.REACT_APP_API_URL || "";

  const response = await fetch(
    `${API_URL}/api/thermal-images/analyze`,
    {
      method: "POST",
      body: formData,
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error(
      "Kuvan analysointi epäonnistui"
    );
  }

  return response.json();
};


 const addFiles = async fileList => {
  if (!fileList?.length || !urlKiinteistoId) return;

  let uploadSubCategory = subCategory;
  if (!subCategory || subCategory === "Kaikki" || subCategory === "Ei aliluokkaa") uploadSubCategory = "";

  const beforeIds = new Set(availableImages.map(i => i.id));

  for (const file of Array.from(fileList)) {

    // NEW: Check if this is a radiometric thermal image
    try {
      const thermalResult = await analyzeThermalImage(file);

      if (thermalResult?.isThermal) {
        console.log("🔥 Thermal image:", file.name);
        console.log("Thermal data:", thermalResult);
      } else {
        console.log("📷 Normal image:", file.name);
      }
    } catch (err) {
      console.log("📷 Normal image or thermal analysis failed:", file.name);
    }

    // EVERYTHING BELOW IS YOUR EXISTING CODE
    try {
      await uploadKiinteistoImage(
        file,
        urlKiinteistoId,
        SYSTEM_USER_ID,
        category,
        uploadSubCategory
      );

      showToast(`Kuva "${file.name}" ladattu onnistuneesti`, "success");

   } catch {
  // Wait 4 seconds and retry once before queuing offline
  await new Promise(r => setTimeout(r, 4000));

  try {
    await uploadKiinteistoImage(
      file,
      urlKiinteistoId,
      SYSTEM_USER_ID,
      category,
      uploadSubCategory
    );

    showToast(
      `Kuva "${file.name}" ladattu onnistuneesti`,
      "success"
    );

  } catch (err) {
    // Server responded -> don't put into offline queue
    if (!err?.isNetworkError) {
      showToast(
        `Kuvan "${file.name}" lataus epäonnistui: ${
          err?.response?.data?.message ||
          err?.message ||
          "tuntematon virhe"
        }`,
        "danger"
      );

      continue;
    }

    // Network failure -> wait and retry once more
    await new Promise(r => setTimeout(r, 4000));

    try {
      await uploadKiinteistoImage(
        file,
        urlKiinteistoId,
        SYSTEM_USER_ID,
        category,
        uploadSubCategory
      );

      showToast(
        `Kuva "${file.name}" ladattu onnistuneesti`,
        "success"
      );

    } catch (err2) {
      if (!err2?.isNetworkError) {
        showToast(
          `Kuvan "${file.name}" lataus epäonnistui: ${
            err2?.response?.data?.message ||
            err2?.message ||
            "tuntematon virhe"
          }`,
          "danger"
        );

        continue;
      }

      showToast(
        `Kuvan "${file.name}" lataus epäonnistui — lisätty jonoon`,
        "danger"
      );

      addFailedImage(
        file,
        category,
        uploadSubCategory,
        urlKiinteistoId
      );
    }
  }
}
  }

  await loadImages(true);

  setAvailableImages(prev => {
    const added = new Set(
      prev
        .filter(i => !beforeIds.has(i.id))
        .map(i => i.id)
    );

    if (added.size) {
      setNewImageIds(added);
      setTimeout(() => setNewImageIds(new Set()), 3000);
    }

    return prev;
  });
};

  const handleDeleteImage = async imgId => { if (!window.confirm("Haluatko varmasti poistaa tämän kuvan?")) return; try { await deleteKiinteistoImage(imgId); setAvailableImages(prev => prev.filter(img => img.id !== imgId)); showToast("Kuva poistettu onnistuneesti", "success"); } catch { showToast("Kuvan poistaminen epäonnistui", "danger"); } };

  const handleSaveAnnotations = async (imgId, newAnnotations) => {
    const img = availableImages.find(i => i.id === imgId);
    if (!img) return;
    try {
      const updatedMetadata = { ...img.metadata, annotations: newAnnotations };
      await updateKiinteistoImage(imgId, { metadata: updatedMetadata, updated_by: SYSTEM_USER_ID });
      setAvailableImages(prev =>
        prev.map(i => i.id === imgId ? { ...i, metadata: updatedMetadata } : i)
      );
      setDrawingImage(null);
      showToast("Merkinnät tallennettu", "success");
    } catch {
      showToast("Merkintöjen tallennus epäonnistui", "danger");
    }
  };

  const handleDescriptionChange = (id, value) => { resizeTextarea(id); setAvailableImages(prev => prev.map(img => img.id === id ? { ...img, metadata: { ...img.metadata, description: value } } : img)); };
  const handleDescriptionBlur = async id => { const img = availableImages.find(i => i.id === id); if (!img) return; try { await updateKiinteistoImage(id, { metadata: img.metadata, updated_by: SYSTEM_USER_ID }); showToast("Kuvan tiedot päivitetty onnistuneesti", "success"); } catch { showToast("Kuvan tietojen päivitys epäonnistui", "danger"); } };
  const handleCategoryChange = async (id, newCategory) => { const img = availableImages.find(i => i.id === id); if (!img) return; if (newCategory === "kansikuva") { const others = availableImages.filter(o => o.id !== id && o.metadata?.category === "kansikuva"); for (const other of others) try { await updateKiinteistoImage(other.id, { metadata: { ...other.metadata, category: "" }, updated_by: SYSTEM_USER_ID }); } catch {} } try { await updateKiinteistoImage(id, { metadata: { ...img.metadata, category: newCategory }, updated_by: SYSTEM_USER_ID }); fetchSasUrlWithCache({ id, azure_blob_name: img.azure_blob_name }, true); setAvailableImages(prev => prev.map(i => i.id === id ? { ...i, metadata: { ...i.metadata, category: newCategory } } : i)); } catch {} };
  const handleSubCategoryChange = async (id, newSubCategory) => { const img = availableImages.find(i => i.id === id); if (!img) return; try { await updateKiinteistoImage(id, { metadata: { ...img.metadata, subCategory: newSubCategory }, updated_by: SYSTEM_USER_ID }); setAvailableImages(prev => prev.map(i => i.id === id ? { ...i, metadata: { ...i.metadata, subCategory: newSubCategory } } : i)); } catch {} };

  const handleSubCategoryChangeForParent = (newSubCategory) => {
    setSubCategory(newSubCategory); // update local UI state
    onChange?.({ ...data, subCategory: newSubCategory, category: category});
  };

  const sortedImages = useMemo(() => {
    const included = availableImages.filter(img => !excludedImages.includes(img.id));
    const excluded = availableImages.filter(img => excludedImages.includes(img.id));
    return [...included, ...excluded];
  }, [availableImages, excludedImages]);

  const includedCount = availableImages.filter(img => !excludedImages.includes(img.id)).length;

  // modalImage is only updated on navigate (openModal/onNavigate) — if we
  // passed it straight to ImageModal, every edit inside the modal's caption
  // textarea would go into availableImages but the modal's `image` prop
  // would stay pointing at the pre-edit object, so the controlled textarea's
  // value never catches up and typed characters appear to do nothing.
  // Re-resolve the currently-open image from the live list every render.
  const currentModalImage = modalImage
    ? sortedImages.find(img => img.id === modalImage.id) || modalImage
    : null;

  // Responsive limit
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDesktop = windowWidth > 768;

  useEffect(() => { 
    // console.log('responsive limit UEF?')
     const handleResize = () => 
        setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize); },
    []);

  const effectiveUiLimit = windowWidth <= 576 ? 4 : uiLimit;

  const start = (page - 1) * effectiveUiLimit;
  const end = start + effectiveUiLimit;
  const paginatedImages = isPaginated ? sortedImages.slice(start, end) : sortedImages;

  useEffect(() => {
    // console.log('Pagination setting UEF')

    if (isPaginated) { setTotalPages(Math.ceil(sortedImages.length / effectiveUiLimit) || 1); 
    if (page > Math.ceil(sortedImages.length / effectiveUiLimit)) setPage(1); }
    else setTotalPages(1);
  }, [isPaginated, availableImages]);

  const handlePdfSwitch = (imgId, checked) => {
    const newExcluded = checked ? excludedImages.filter(id => id !== imgId) : [...excludedImages, imgId];
    localExcludedChangeRef.current = true;
    setExcludedImages(newExcluded);
    onChange?.({ ...data, excludedImages: newExcluded, propertyId: urlKiinteistoId });
  };

// ---------------- Pagination Component ----------------
const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const isMobile = windowWidth <= 576; // mobile breakpoint

  return (
    <div className="text-center my-3">
      <div className="d-flex flex-wrap justify-content-center gap-1">
        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <i className="bi bi-arrow-left"></i>
        </button>

        {!isMobile &&
          Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={`btn btn-sm ${page === i + 1 ? "btn-primary text-white" : "btn-outline-secondary"}`}
              onClick={() => onPageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))
        }

        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <i className="bi bi-arrow-right"></i>
        </button>
      </div>

      <div className="mt-2">Sivu {page} / {totalPages}</div>
    </div>
  );
};

  return (
    <div key={blockPath} style={{ position: 'relative' }}>
      <style>{`
        .imgblock-subcategory { font-size: 0.875rem; padding: 3px 8px; }
        .imgblock-lock { font-size: 0.875rem; padding: 2px 6px; }
        .imgblock-category-toggle, .imgblock-category-item { font-size: 0.875rem !important; }
        .imgblock-controls-sticky {
          position: relative;
        }
        @media (max-width: 767px) {
          .imgblock-subcategory { font-size: 0.72rem; padding: 1px 5px; }
          .imgblock-lock { font-size: 0.75rem; padding: 1px 5px; }
          .imgblock-category-toggle, .imgblock-category-item { font-size: 0.72rem !important; padding: 3px 8px !important; }
          .imgblock-controls-sticky { position: relative; top: auto; }
        }
        .ib-card { transition: box-shadow .12s ease; border-radius: 10px; }
        .ib-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.07); }
        .ib-toolbar { position: absolute; left: 0; right: 0; bottom: 0; height: 32px; background: linear-gradient(0deg, rgba(0,0,0,0.58), rgba(0,0,0,0)); display: flex; align-items: center; justify-content: space-between; padding: 0 6px 4px; }
        .ib-portrait-tag { font-size: 0.6rem; font-weight: 600; color: #fff; letter-spacing: 0.03em; }
        .ib-toolbar-actions { display: flex; align-items: center; gap: 10px; margin-left: auto; }
        .ib-tool-btn { border: none; background: none; display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 4px; }
        .ib-tool-btn svg { pointer-events: none; }
        .ib-chev { color: #6e6e73; }
        .ib-select { border: none; background: transparent; color: #8e8e93; font-size: 0.68rem; padding: 0; cursor: pointer; width: 100%; }
        .ib-select:disabled { opacity: 0.5; cursor: default; }
        .ib-caption { border: none; border-bottom: 1px solid #ededef; background: transparent; resize: none; padding: 0 0 6px; outline: none; width: 100%; font-family: inherit; white-space: pre-wrap; word-wrap: break-word; overflow-x: hidden; }
        .ib-caption:focus { border-bottom-color: #1d7a46; }
        .ib-switch { position: relative; display: inline-flex; align-items: center; gap: 6px; cursor: pointer; font-size: 0.72rem; color: #48484a; }
        .ib-switch input { position: absolute; opacity: 0; width: 1px; height: 1px; }
        .ib-switch-track { position: relative; width: 28px; height: 16px; border-radius: 999px; background: #d8d8dc; transition: background .15s; flex-shrink: 0; }
        .ib-switch-track::after { content: ""; position: absolute; width: 12px; height: 12px; border-radius: 50%; background: #fff; top: 2px; left: 2px; transition: transform .15s; }
        .ib-switch input:checked + .ib-switch-track { background: #1d7a46; }
        .ib-switch input:checked + .ib-switch-track::after { transform: translateX(12px); }
        .ib-switch input:focus-visible + .ib-switch-track { outline: 2px solid #1d7a46; outline-offset: 2px; }
        .ib-switch:hover .ib-switch-track { background: #c2c2c8; }
        .ib-switch:hover input:checked + .ib-switch-track { background: #176339; }
        @keyframes imgblock-flash-in {
          0%   { opacity: 0; transform: translateX(-50%) translateY(16px) scale(0.9); }
          15%  { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
          75%  { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
          100% { opacity: 0; transform: translateX(-50%) translateY(8px) scale(0.95); }
        }
        .imgblock-flash {
          position: fixed; bottom: 90px; left: 50%;
          transform: translateX(-50%);
          z-index: 9999; pointer-events: none;
          animation: imgblock-flash-in 2.5s ease forwards;
        }
      `}</style>
      {flashUrls.length > 0 && createPortal(
        <div className="imgblock-flash" style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          {flashUrls.map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={url} alt="" style={{ height: 72, width: 72, objectFit: 'cover', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.35)', border: '2px solid #198754' }} />
              <div style={{ position: 'absolute', bottom: 4, right: 4, background: '#198754', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-check" style={{ color: '#fff', fontSize: '0.7rem', lineHeight: 1 }}></i>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
      <ToastMessage show={toast.show} message={toast.message} bg={toast.bg} onClose={() => setToast({ ...toast, show: false })} />



      {/* <div>
        <label>Lataamattomat kuvat</label>

{failedImages.length > 0 &&
  <div className="d-flex flex-wrap gap-2">
    {failedImages.map((imageFailed, i) => {
      const file = imageFailed.file;
      const previewUrl = file ? URL.createObjectURL(file) : null;

      return (
        <div key={file?.name || i} className="border p-1 position-relative" style={{ width: 120 }}>
          {previewUrl && (
            <img
              src={previewUrl}
              alt={file.name}
              className="img-fluid"
              style={{ objectFit: "cover", height: 100, width: "100%" }}
            />
          )}
          <div className="mt-1 small text-truncate">{file?.name}</div>
        </div>
      );
    })}
  </div>
}
      </div> */}

      {/* Category & Subcategory */}
      <div className="imgblock-controls-sticky d-flex flex-column gap-1 mb-2" style={{ paddingTop: '4px', paddingBottom: '4px' }}>
        <div className="d-flex flex-column align-items-center gap-1">
          <div className="d-flex align-items-center gap-1">
            <Dropdown onSelect={val => { const newCat = val || ""; setCategory(newCat); setSubCategory(null); setPage(1); onChange?.({ ...data, category: newCat, subCategory: null }); }}>
              <Dropdown.Toggle variant="outline-secondary" className="imgblock-category-toggle" style={{ padding: '4px 10px' }}>
                {category || "Kaikki"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="" className="imgblock-category-item">Kaikki</Dropdown.Item>
                {mode === "gallery" ? (
                  <>
                    {mainCategories.filter(c => c !== "Ei kategoriaa" && c !== "Kansikuva" && c !== "Liitteet" && c !== "Rappausnäytteet" && !DYNAMIC_IMAGE_CATEGORIES.has(c)).map(cat => (
                      <Dropdown.Item key={cat} eventKey={cat} className="imgblock-category-item">{cat}</Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    {mainCategories.filter(c => DYNAMIC_IMAGE_CATEGORIES.has(c)).map(cat => (
                      <Dropdown.Item key={cat} eventKey={cat} className="imgblock-category-item">{cat}</Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item eventKey="Kansikuva" className="imgblock-category-item">Kansikuva</Dropdown.Item>
                    <Dropdown.Item eventKey="Liitteet" className="imgblock-category-item">Liitteet</Dropdown.Item>
                    <Dropdown.Item eventKey="Rappausnäytteet" className="imgblock-category-item">Rappausnäytteet</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item eventKey="Ei kategoriaa" className="imgblock-category-item">Ei kategoriaa</Dropdown.Item>
                  </>
                ) : showKuntoarvioCategories ? (
                  <>
                    {mainCategories.filter(c => DYNAMIC_IMAGE_CATEGORIES.has(c)).map(cat => (
                      <Dropdown.Item key={cat} eventKey={cat} className="imgblock-category-item">{cat}</Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item eventKey="Kansikuva" className="imgblock-category-item">Kansikuva</Dropdown.Item>
                    <Dropdown.Item eventKey="Liitteet" className="imgblock-category-item">Liitteet</Dropdown.Item>
                    <Dropdown.Item eventKey="Rappausnäytteet" className="imgblock-category-item">Rappausnäytteet</Dropdown.Item>
                  </>
                ) : (
                  <>
                    {mainCategories.filter(c => c !== "Ei kategoriaa" && c !== "Kansikuva" && !DYNAMIC_IMAGE_CATEGORIES.has(c)).map(cat => (
                      <Dropdown.Item key={cat} eventKey={cat} className="imgblock-category-item">{cat}</Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item eventKey="Kansikuva" className="imgblock-category-item">Kansikuva</Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        {category && (
          <>
            <div className="d-flex flex-column align-items-center gap-1">
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', width: '100%', gap: '5px' }}>
                {[null, ...effectiveSubCategories].map(sub => {
                  const active = sub === null ? !subCategory : subCategory === sub;
                  return (
                    <span
                      key={sub ?? '__all__'}
                      className="imgblock-subcategory"
                      onClick={() => handleSubCategoryChangeForParent(sub)}
                      style={{
                        lineHeight: '1.3',
                        cursor: 'pointer',
                        color: active ? '#146c43' : '#555',
                        fontWeight: active ? '600' : '400',
                        border: `1px solid ${active ? '#198754' : '#ccc'}`,
                        borderRadius: '4px',
                        backgroundColor: active ? '#d1e7dd' : 'transparent',
                      }}
                    >{sub ?? 'Kaikki'}</span>
                  );
                })}
              </div>
            </div>
          </>
        )}
        <div className="d-flex gap-2 mt-2 justify-content-center">
          <label className="btn btn-outline-primary text-center" style={{ fontSize: '0.95rem', padding: '6px 16px' }}>
            <i className="bi bi-folder-symlink-fill me-1"></i>Tuo kuvia
            <input type="file" multiple accept="image/*" style={{ display: "none" }} onChange={e => addFiles(e.target.files)} />
          </label>
          <label className="btn btn-outline-success text-center" style={{ fontSize: '0.95rem', padding: '6px 16px' }}>
            <i className="bi bi-camera-fill me-1"></i>Ota kuva
            <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => addFiles(e.target.files)} />
          </label>
        </div>
      </div>

      {/* Pagination toggle + hide all */}
      <div className="mb-3 d-flex justify-content-center gap-2">
        <button
          className="btn btn-sm btn-outline-primary"
          title={isPaginated ? "Sivuttamaton näkymä" : "Näytä sivuittain"}
          onClick={() => setIsPaginated(prev => !prev)}
        >
          <i className={`bi ${isPaginated ? "bi-layout-text-window" : "bi-files"}`} />
        </button>
        {mode === "report" && availableImages.length > 0 && (() => {
          const allHidden = availableImages.every(i => excludedImages.includes(i.id));
          return (
            <button
              className={`btn btn-sm ${allHidden ? "btn-secondary" : "btn-outline-secondary"}`}
              title={allHidden ? "Näytä kaikki PDF:ssä" : "Piilota kaikki PDF:stä"}
              onClick={() => setToggleAllConfirm(allHidden ? "show" : "hide")}
            >
              <i className={`bi ${allHidden ? "bi-eye" : "bi-eye-slash"}`} />
            </button>
          );
        })()}
      </div>

      {/* Top Pagination */}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Loading */}
      {isLoading && (
        <div className="text-center my-3">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
          <div className="mt-2 fw-semibold">Haetaan kiinteistön kuvia...</div>
        </div>
      )}

      {/* Responsive image grid */}
      <div className={`row g-2 ${mode === 'gallery' ? 'row-cols-2 row-cols-md-4 row-cols-lg-6' : 'row-cols-2 row-cols-md-3 row-cols-lg-4'}`} ref={gridRef}>


        {paginatedImages.map((img, i) => {
          const globalIndex = (page - 1) * effectiveUiLimit + i;
          const isExcluded = excludedImages.includes(img.id);
          const isLastIncluded = mode === "report" && !isExcluded && globalIndex === includedCount - 1;
          const imgSrc = imageUrls[img.id] || undefined;
          const imgCategory = normalizeCategory(img.metadata?.category) || undefined;
          const imgSubcategories = DYNAMIC_IMAGE_CATEGORIES.has(imgCategory)
            ? (dynamicSubCategoriesByCategory[imgCategory] || [])
            : (IMAGE_CATEGORIES[imgCategory] || []);
          return (
            <React.Fragment key={img.id}>
            <div
              className={`col`}
              onDragOver={isDesktop ? e => handleDragOver(e, globalIndex) : undefined}
              onDragLeave={isDesktop ? () => setDragOverIndex(null) : undefined}
              onDrop={isDesktop ? e => handleDrop(e, globalIndex) : undefined}
            >
              <div
                className="ib-card position-relative"
                style={{
                  border: '1px solid #e5e5ea',
                  background: '#fff',
                  opacity: dragSrcIndex === globalIndex ? 0.35 : isExcluded ? 0.65 : undefined,
                  outline: dragOverIndex === globalIndex ? '2px solid #198754' : undefined,
                  outlineOffset: dragOverIndex === globalIndex ? '2px' : undefined,
                }}
              >
                {isLastIncluded && (
                  <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 3, background: '#198754', boxShadow: '0 0 8px #198754, 0 0 18px rgba(25,135,84,0.5)', zIndex: 5, borderRadius: '0 4px 4px 0' }} />
                )}

                {/* Image, with rotate/delete + portrait flag collapsed into one toolbar strip docked to its bottom edge, instead of always-on colored buttons scattered on the corners.
                    Only this wrapper is draggable (not the whole card) — dragging used to be
                    initiatable from anywhere on the card (dropdowns, switch, blank space),
                    which fought normal interaction with those controls. */}
                <div
                  draggable={isDesktop ? true : undefined}
                  onDragStart={isDesktop ? e => handleDragStart(e, globalIndex) : undefined}
                  onDragEnd={isDesktop ? () => { setDragSrcIndex(null); setDragOverIndex(null); } : undefined}
                  style={{ position: 'relative', aspectRatio: '4/3', background: '#f3f4f6', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', borderRadius: '9px 9px 0 0', cursor: isDesktop ? (dragSrcIndex === globalIndex ? 'grabbing' : 'grab') : undefined }}>
                  {imgSrc ?
                    <img
                      src={imgSrc}
                      alt={img.metadata?.description || ""}
                      // Images are natively draggable by default, which
                      // fights the card's own draggable={true} reorder drag
                      // — the browser preps a native "drag this image out"
                      // ghost/cursor on hover, which is what made the mouse
                      // cursor flicker/disappear over the grid. Disabling it
                      // here doesn't affect the card-level reorder drag,
                      // which is driven by the ancestor .col div instead.
                      draggable={false}
                      loading="lazy"
                      decoding="async"
                      onError={e => {
                        console.warn("Image load failed", e.target.src);
                        // Could be a blob: URL evicted from the shared
                        // memoryCache LRU (capped at 150), or a corrupted
                        // raw blob persisted in IndexedDB from an earlier
                        // failed fetch (e.g. an Azure error response body
                        // that got cached as if it were the image) — either
                        // way, force a fresh network fetch rather than
                        // re-reading the same cache that just failed.
                        if (!imgRetriedRef.current.has(img.id)) {
                          imgRetriedRef.current.add(img.id);
                          fetchSasUrlWithCache(img, true);
                        }
                      }}
                      className="object-fit-cover"
                      style={{ width: '100%', height: '100%', transform: `rotate(${img.metadata?.rotation || 0}deg)`, cursor: 'pointer' }}
                      onClick={() => openModal(img)}
                      onLoad={(e) => {
                        const { naturalWidth, naturalHeight } = e.target;
                        // Use rotation-aware calculation
                        setPortraitImages(prev => ({
                          ...prev,
                          [img.id]: isPortraitWithRotation(naturalWidth, naturalHeight, img.metadata?.rotation || 0)
                        }));
                      }}
                    />
                  :

                    <i
                      className="bi bi-building-fill-down text-secondary"
                      style={{
                        width: '100%',
                        height: '100%',
                        fontSize: '1.5rem',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center'
                      }}
                    >
                        Ladataan...
                    </i>

                  }
                  {img.metadata?.annotations?.length > 0 && imgSrc && (
                    <svg viewBox="0 0 1 1" preserveAspectRatio="none"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                      {img.metadata.annotations.map((ann, ai) => {
                        if (ann.type === 'circle') {
                          return <circle key={ai} cx={ann.cx} cy={ann.cy} r={ann.r} stroke={ann.color} strokeWidth="0.008" fill="none" />;
                        } else if (ann.type === 'arrow') {
                          const dx = ann.x2 - ann.x1, dy = ann.y2 - ann.y1;
                          const len = Math.sqrt(dx * dx + dy * dy);
                          if (len < 0.01) return null;
                          const headLen = Math.min(0.07, len * 0.35);
                          const angle = Math.atan2(dy, dx);
                          const hx1 = ann.x2 - headLen * Math.cos(angle - Math.PI / 6);
                          const hy1 = ann.y2 - headLen * Math.sin(angle - Math.PI / 6);
                          const hx2 = ann.x2 - headLen * Math.cos(angle + Math.PI / 6);
                          const hy2 = ann.y2 - headLen * Math.sin(angle + Math.PI / 6);
                          return (
                            <g key={ai}>
                              <line x1={ann.x1} y1={ann.y1} x2={ann.x2} y2={ann.y2} stroke={ann.color} strokeWidth="0.008" strokeLinecap="round" />
                              <polygon points={`${ann.x2},${ann.y2} ${hx1},${hy1} ${hx2},${hy2}`} fill={ann.color} />
                            </g>
                          );
                        }
                        return null;
                      })}
                    </svg>
                  )}
                  {/* Not gated on imgSrc — delete (and rotate) must stay available
                      even while the image is still loading, same as before. */}
                  <div className="ib-toolbar">
                    {portraitImages[img.id]
                      ? <span className="ib-portrait-tag" onClick={() => openModal(img)} style={{ cursor: 'pointer' }}>PYSTYKUVA</span>
                      : <span />}
                    <div className="ib-toolbar-actions">
                      <button type="button" className="ib-tool-btn" aria-label="Käännä kuvaa" title="Käännä" onClick={() => handleRotateImage(img.id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 1 3 6.7"/><path d="M3 17v-5h5"/></svg>
                      </button>
                      <button type="button" className="ib-tool-btn" aria-label="Poista kuva" title="Poista" onClick={() => handleDeleteImage(img.id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-2 d-flex flex-column gap-2">
                  {/* Description */}
                  <textarea
                    ref={el => (textareaRefs.current[img.id] = el)}
                    value={img.metadata?.description || ""}
                    onChange={e => handleDescriptionChange(img.id, e.target.value)}
                    onBlur={() => handleDescriptionBlur(img.id)}
                    // The card's outer .col div is draggable (for reorder) —
                    // without stopping this, a mouse-drag to select caption
                    // text gets hijacked into a whole-card drag instead.
                    onMouseDown={e => e.stopPropagation()}
                    className="lato-textarea ib-caption"
                    rows={2}
                  />

                  {/* Category / Subcategory */}
                  <select value={imgCategory} onChange={e => handleCategoryChange(img.id, e.target.value || null)} className="ib-select">
                    {(showKuntoarvioCategories
                      ? mainCategories.filter(c => DYNAMIC_IMAGE_CATEGORIES.has(c) || c === 'Kansikuva' || c === 'Liitteet' || c === 'Rappausnäytteet')
                      : mainCategories.filter(c => c !== 'Ei kategoriaa')
                    ).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                  <select value={img.metadata?.subCategory || ""} onChange={e => handleSubCategoryChange(img.id, e.target.value)} className="ib-select" disabled={!imgCategory}>
                    {imgSubcategories.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                  </select>

                  {/* Reorder / PDF */}
                  <div className="imgblock-card-controls d-flex flex-nowrap gap-2 mt-1 justify-content-between align-items-center">
                    {globalIndex === 0 || globalIndex === includedCount
                      ? <span style={{ display: 'inline-block', width: 22 }} />
                      : <button type="button" className="ib-tool-btn ib-chev" aria-label="Siirrä vasemmalle" onClick={() => moveImage(globalIndex, -1)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
                        </button>
                    }

                    {mode === "report" && (
                      <label className="ib-switch">
                        <input type="checkbox" checked={!excludedImages.includes(img.id)} onChange={e => handlePdfSwitch(img.id, e.target.checked)} />
                        <span className="ib-switch-track" />
                        PDF
                      </label>
                    )}

                    {globalIndex === sortedImages.length - 1 || globalIndex === includedCount - 1
                      ? <span style={{ display: 'inline-block', width: 22 }} />
                      : <button type="button" className="ib-tool-btn ib-chev" aria-label="Siirrä oikealle" onClick={() => moveImage(globalIndex, 1)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                        </button>
                    }
                  </div>

                </div>
              </div>
            </div>
            </React.Fragment>
          );
        })}
      </div>

        {/* Upload buttons */}
      {/* <div className="row g-2 my-2">
        <div className="col">
          <label className="btn btn-primary w-100 text-center py-1">
            <i class="bi bi-folder-symlink-fill me-1"></i>Tuo kuvia
            <input type="file" multiple accept="image/*" style={{ display: "none" }} onChange={e => addFiles(e.target.files)} />
          </label>
        </div>
        <div className="col">
          <label className="btn btn-secondary w-100 text-center py-1">
            <i class="bi bi-camera-fill me-1"></i>Ota kuva
            <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => addFiles(e.target.files)} />
          </label>
        </div>
      </div>

 */}

      
      {/* Bottom Pagination */}
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      



      {modalOpen ? <>      <ImageModal
        open={modalOpen}
        image={currentModalImage}
        images={sortedImages}
        imageUrls={imageUrls}
        azureBase={azureBase}
        onClose={closeModal}
        onNavigate={setModalImage}
        onAnnotate={() => { closeModal(); setDrawingImage(modalImage); }}
        fetchSasUrlWithCache={fetchSasUrlWithCache}
        onDescriptionChange={handleDescriptionChange}
        onDescriptionBlur={handleDescriptionBlur}
      /></> : <></>}

      {drawingImage && (
        <AnnotationModal
          image={drawingImage}
          imageUrl={imageUrls[drawingImage.id]}
          onSave={(anns) => handleSaveAnnotations(drawingImage.id, anns)}
          onClose={() => setDrawingImage(null)}
          fetchSasUrlWithCache={fetchSasUrlWithCache}
        />
      )}

      <ConfirmModal
        show={!!toggleAllConfirm}
        onClose={() => setToggleAllConfirm(null)}
        onConfirm={() => {
          if (toggleAllConfirm === "hide") {
            const allIds = availableImages.map(i => i.id);
            localExcludedChangeRef.current = true;
            setExcludedImages(allIds);
            onChange?.({ ...data, excludedImages: allIds, propertyId: urlKiinteistoId });
          } else {
            localExcludedChangeRef.current = true;
            setExcludedImages([]);
            onChange?.({ ...data, excludedImages: [], propertyId: urlKiinteistoId });
          }
          setToggleAllConfirm(null);
        }}
        title={toggleAllConfirm === "hide" ? "Piilota kaikki kuvat" : "Näytä kaikki kuvat"}
        message={toggleAllConfirm === "hide" ? "Piilotetaanko kaikki kuvat PDF:stä?" : "Näytetäänkö kaikki kuvat PDF:ssä?"}
        confirmText={toggleAllConfirm === "hide" ? "Piilota kaikki" : "Näytä kaikki"}
        cancelText="Peru"
        confirmVariant={toggleAllConfirm === "hide" ? "danger" : "success"}
      />

    </div>
  );
}
